import { Form, message } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { FilteredMetadata } from '@api/useFreight/useFreight'
import type {
  CreateAutomaticAccessorialPayload,
  AutomaticAccessorial,
  Ratebook,
  BasicFeeType,
  ThresholdFeeType,
} from '../../../../../../../api/useFreight/types'
import type { FormValues, CreateAccessorialFormProps } from '../components/types'
import { isPercentageFeeType, isThresholdFeeType, isTimeIntervalFeeType } from '../utils'
import { DEFAULT_TIME_INTERVAL, SERVICE_RULE_DEFAULTS } from '../components/constants'
import api from '@utils/api'
import { isDefined } from '@/utils/general'

export function useAccessorialForm({
  ratebook,
  onSuccess,
  editingAccessorial,
  createMutation,
}: CreateAccessorialFormProps) {
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  function handleSubmit(values: FormValues, metadataOptions?: FilteredMetadata) {
    try {
      const cascaderValue = (values.selected_products || []) as string[][]
      const productIds: string[] = []
      const productHierarchy = metadataOptions?.product_hierarchy

      if (productHierarchy && cascaderValue.length > 0) {
        for (const path of cascaderValue) {
          if (path.length === 1) {
            const categoryProducts = productHierarchy[path[0]]
            if (categoryProducts) {
              productIds.push(...categoryProducts.map((p) => p.id))
            }
          } else if (path.length === 2) {
            productIds.push(path[1])
          }
        }
      }

      const rate = isPercentageFeeType(values.fee_type)
        ? Number((values.rate / 100).toFixed(5))
        : Number(values.rate.toFixed(5))

      const allOriginIds = [...(values.origin_terminals || []), ...(values.origin_sites || [])]

      const allDestinationIds = [...(values.destination_terminals || []), ...(values.destination_sites || [])]

      const payload: CreateAutomaticAccessorialPayload = {
        _id: editingAccessorial?._id || null,
        name: editingAccessorial ? editingAccessorial.name : (values.name as string),
        counterparty_id: ratebook.counterparty_id,
        book_type: ratebook.book_type as 'Cost' | 'Revenue',
        product_ids: [...new Set(productIds)],
        destination_ids: allDestinationIds,
        origin_ids: allOriginIds,
        fee_type: {
          type: values.fee_type as BasicFeeType | ThresholdFeeType,
          threshold:
            isThresholdFeeType(values.fee_type) || isTimeIntervalFeeType(values.fee_type)
              ? Number(values.threshold)
              : null,
          interval: isTimeIntervalFeeType(values.fee_type)
            ? values.interval
              ? Number(values.interval)
              : DEFAULT_TIME_INTERVAL
            : null,
          max_intervals:
            isTimeIntervalFeeType(values.fee_type) && isDefined(values.max_intervals)
              ? Number(values.max_intervals)
              : null,
        },
        rates: [
          {
            effective_from: values.effective_from
              ? values.effective_from.format('YYYY-MM-DD')
              : dayjs().format('YYYY-MM-DD'),
            effective_to:
              values.has_effective_to && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : null,
            rate,
            created_by: null,
            created_on: new Date().toISOString(),
          },
        ],
        disabled: false,
        requires_approval: values.requires_approval || false,
        requires_reason_code: false,
        exclude_from_invoice: values.exclude_from_invoice || false,
        linked_special_pay_config: values.linked_special_pay_config
          ? [
              {
                sp_id: values.linked_special_pay_config.value,
                name: values.linked_special_pay_config.label,
                link_date: dayjs().utc().toISOString(),
                link_type: values.link_type,
              },
            ]
          : [],
        service_rule: values.service_rule_form?.enabled
          ? {
              type: values.service_rule_form?.type as 'Weekend' | 'Holiday' | 'Night',
              threshold_type: values.service_rule_form?.threshold_type,
            }
          : null,
      }

      createMutation.mutate(payload, {
        onSuccess: () => {
          message.success(
            editingAccessorial
              ? 'Automatic accessorial updated successfully'
              : 'Automatic accessorial created successfully'
          )
          queryClient.invalidateQueries({
            queryKey: ['automaticAccessorials', ratebook._id],
          })
          onSuccess()
        },
        onError: (error) => {
          console.error('Error creating/updating accessorial:', error)
          message.error(
            editingAccessorial ? 'Failed to update automatic accessorial' : 'Failed to create automatic accessorial'
          )
        },
      })
    } catch (error) {
      console.error('Error preparing accessorial submission:', error)
      message.error('An unexpected error occurred while preparing the submission.')
    }
  }

  return {
    form,
    handleSubmit,
    initialValues: {
      service_rule_form: SERVICE_RULE_DEFAULTS,
      effective_from: dayjs(),
    },
  }
}
